import { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// @mui material components
import Switch from '@mui/material/Switch';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Argon Dashboard 2 MUI components
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonInput from 'components/ArgonInput';
import ArgonButton from 'components/ArgonButton';

// Authentication layout components
import IllustrationLayout from 'layouts/authentication/components/IllustrationLayout';
import franklyLogo from '../../../assets/images/frankly_login.png';

// --- NEW: Import the refactored service functions ---
import { login, getStatus } from 'services/user';
import { UserContext } from 'context/userContext';

function SignIn() {
  // The "rememberMe" switch can still be used to tell the server to issue a longer-lived cookie,
  // but we won't implement that on the server side for now. The UI can remain.
  const [rememberMe, setRememberMe] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { userId, setUserId } = useContext(UserContext);

  // Snackbar state remains the same - it's good!
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const navigate = useNavigate();

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const handleUsername = (e) => {
    setUsername(e.target.value);
  };

  const handlePassword = (e) => {
    setPassword(e.target.value);
  };

  // --- REFACTORED: Sign-in handler ---
  const handleSignIn = async () => {
    if (!username || !password) {
      setSnackbarMessage('Username and password are required.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      const { user } = await login(username, password);
      if (!user || !user._id) {
        throw new Error('Invalid credentials or error fetching user data.');
      }

      setSnackbarMessage('Login successful! Redirecting...');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      setTimeout(() => {
        setUserId(user._id);

        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      setSnackbarMessage(error.message || 'An unexpected error occurred.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} variant="filled" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ArgonBox
        width="-webkit-fill-available"
        height="-webkit-fill-available"
        sx={{
          backgroundImage: `url(${franklyLogo})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'absolute',
          zIndex: -10,
        }}
      />
      <IllustrationLayout
        title="Sign In"
        description="Enter your email and password to sign in"
        illustration={{
          title: '"Frankly Solutions - LifeCycle"',
          description: '',
        }}
      >
        <ArgonBox
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSignIn();
          }}
        >
          <ArgonBox mb={2}>
            <ArgonInput
              type="text"
              placeholder="Email or Username"
              size="large"
              value={username}
              onChange={handleUsername}
            />
          </ArgonBox>
          <ArgonBox mb={2}>
            <ArgonInput
              type="password"
              placeholder="Password"
              size="large"
              value={password}
              onChange={handlePassword}
            />
          </ArgonBox>
          <ArgonBox display="flex" alignItems="center">
            <Switch checked={rememberMe} onChange={handleSetRememberMe} />
            <ArgonTypography
              variant="button"
              fontWeight="regular"
              onClick={handleSetRememberMe}
              sx={{ cursor: 'pointer', userSelect: 'none' }}
            >
              Remember me
            </ArgonTypography>
          </ArgonBox>
          <ArgonBox mt={4} mb={1}>
            <ArgonButton
              type="submit" // Use type="submit" for better form handling (e.g., pressing Enter)
              color="info"
              size="large"
              fullWidth
              style={{ border: '1px solid #11cdef', padding: '12px' }}
            >
              Sign In
            </ArgonButton>
          </ArgonBox>
          <ArgonBox mt={3} textAlign="center">
            <ArgonTypography variant="button" color="text" fontWeight="regular">
              Don't have an account?{' '}
              <ArgonTypography
                component={Link}
                to="/authentication/sign-up"
                variant="button"
                color="info"
                fontWeight="medium"
              >
                Sign up
              </ArgonTypography>
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      </IllustrationLayout>
    </>
  );
}

export default SignIn;
